import { useState } from "react";
import { COPY, INLINE_INPUT_CLASS } from "@/constants/campaignContacts";
import { recipientSendDateError } from "./addRecipients-helper";
import {
  formatSendInstant,
  maxSendDateInput,
  minSendDateInput,
  zonedDateInput,
} from "./campaignContacts-helper";
import type { CampaignContactRow } from "./types";
import { useInlineCommit } from "./useInlineCommit";

interface ContactSendDateEditorProps {
  contact: CampaignContactRow;
  readOnly: boolean;
  /** The campaign's zone: the day shown and the "after today" bound are read in it. */
  timeZone: string;
  /** The campaign's close instant: the close day and later are refused. Null: no upper bound. */
  expireDate: string | null;
  /** Called with a future YYYY-MM-DD; a rejection makes the same value retryable. */
  onCommit: (contact: CampaignContactRow, date: string) => void | Promise<void>;
  /** Called with the refusal message when the picked date is not after today or not before the close. */
  onInvalid: (message: string) => void;
}

/**
 * Inline date input for one recipient's send date. Owns its draft and, through `useInlineCommit`,
 * the last committed value (the row prop stays stale until the refetch lands). Commits on
 * blur when the value differs (`onChange` only updates the draft).
 */
export default function ContactSendDateEditor({
  contact,
  readOnly,
  timeZone,
  expireDate,
  onCommit,
  onInvalid,
}: ContactSendDateEditorProps) {
  const initial = zonedDateInput(contact.sendDate, timeZone) ?? "";
  // The send instant with its zone label, so the bare day in the cell says which zone it is in.
  const title = initial ? formatSendInstant(contact.sendDate, timeZone) : undefined;
  const [value, setValue] = useState(initial);
  const inline = useInlineCommit(initial);

  async function commit(next: string) {
    if (next === inline.baseline()) return;
    const refusal = recipientSendDateError(next, new Date(), expireDate, timeZone);
    if (refusal !== null) {
      setValue(inline.baseline());
      return onInvalid(refusal);
    }
    // A rejected write puts the input back on the restored value, so re-picking retries.
    await inline.commit(next, () => onCommit(contact, next), setValue);
  }

  return (
    <input
      type="date"
      value={value}
      min={minSendDateInput(timeZone)}
      max={maxSendDateInput(expireDate, timeZone)}
      readOnly={readOnly}
      title={title}
      aria-label={COPY.actions.sendDateLabel(contact.name)}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => commit(value)}
      className={INLINE_INPUT_CLASS}
    />
  );
}
